import { http, httpDownloadAs } from 'shared/services/httpService';
import appSettings from 'shared/settings/appSettings';
import { CleverString } from 'components/ui/CleverString';
import { FatigueAssessmentReportQuery, LoginHistoryReportQuery, LoginHistory, DocumentDepartment, GNHExemptionReportQuery, TakeFiveReportQuery, TrainEventRecordReportQuery } from 'shared/models/api/models';
const basePath = '/api/reporting/';

const fullyQualifiedAndEncodedURL = (relativeUri: string) => {
    return new URL(basePath + encodeURI(relativeUri), appSettings.api.portalApi).toString();
};

const getUsersByNamePath = (name: string) => { return fullyQualifiedAndEncodedURL('usersByName/' + name); };
const getSupervisorsByNamePath = (name: string) => { return fullyQualifiedAndEncodedURL('supervisorsByName/' + name); };
const getUsersByPublisherNamePath = (name: string) => { return fullyQualifiedAndEncodedURL('usersByPublisherName/' + name); };
const getDocumentsByNamePath = (name: string) => { return fullyQualifiedAndEncodedURL('documentsByName/' + name); };
const getAllDocumentsPath = () => { return fullyQualifiedAndEncodedURL('allDocuments') };
const getAllD2DocumentsPath = () => { return fullyQualifiedAndEncodedURL('allD2Documents') };
const getUsersSupervisedByCurrentUserPath = () => { return fullyQualifiedAndEncodedURL('usersSupervisedByCurrentUser') };
const getUsersPath = () => { return fullyQualifiedAndEncodedURL('users') };
const getSupervisorsPath = () => { return fullyQualifiedAndEncodedURL('supervisors') };
const getPublishersPath = () => { return fullyQualifiedAndEncodedURL('publishers') };
const getUsersBySupervisorPath = (id: number) => { return fullyQualifiedAndEncodedURL('usersBySupervisor/' + id); };
const getUsersByDepartmentPath = (name: string) => { return fullyQualifiedAndEncodedURL('usersByDepartment/' + name); };
const getUsersByStatusPath = (name: string) => { return fullyQualifiedAndEncodedURL('usersByStaus/' + name); };
const getDepartmentsPath = () => { return fullyQualifiedAndEncodedURL('departments'); };
const getParentDepartmentsPath = () => { return fullyQualifiedAndEncodedURL('parentdepartments'); };
const getDocumentTypesPath = () => { return fullyQualifiedAndEncodedURL('documentTypes'); };
const getUserStatusesPath = () => { return fullyQualifiedAndEncodedURL('userStatuses'); };
const getDocumentStatusesPath = () => { return fullyQualifiedAndEncodedURL('documentStatuses'); };
const getUsersDocumentsPath = () => { return fullyQualifiedAndEncodedURL('usersDocuments'); };
const getD2DocumentsPath = () => { return fullyQualifiedAndEncodedURL('d2Documents'); };
const downloadResultsPath = () => { return fullyQualifiedAndEncodedURL('usersDocumentsExport'); };
const downloadFatigueAssessmentResultsPath = () => { return fullyQualifiedAndEncodedURL('fatigueassessmentsexport'); };
const getLoginHistoryResultsPath = () => { return fullyQualifiedAndEncodedURL('loginhistory'); };
const downloadLoginHistoryResultsPath = () => { return fullyQualifiedAndEncodedURL('loginhistoryexport'); };
const downloadFullComplianceReportPath = () => { return fullyQualifiedAndEncodedURL('fullComplianceReport'); };
const downloadGNHExemptionResultsPath = () => { return fullyQualifiedAndEncodedURL('gnhexemptionsexport'); };
const downloadTakeFiveResultsPath = () => { return fullyQualifiedAndEncodedURL('takefivesexport'); };
const downloadTrainEventReportPath = () => { return fullyQualifiedAndEncodedURL('trainEventRecordExport'); };

export interface DocumentMetadata {
    title: string,
    documentNumber: string
}

export interface DocumentMetadataExtended extends DocumentMetadata {
    effectiveFromDate?: Date,
    expiryDate?: Date,
    modifiedDate?: Date,
    reviewDate?: Date,
    releaseNotes?: string
}

export interface Document<T> {
    id: number,    
    type: string,
    subType: string,
    fileName: string,
    metadata: T,
}

export interface DocumentExtended extends Document<DocumentMetadataExtended> {
    status: string,
    uploadedBy: string,
    uploadedDate: Date,
    relatedDocuments: string[]
}

export interface IEntity {
    id: number
}

export interface DocumentType extends IEntity {
    id: number,
    name: string,
    parentId: number,
    subTypes?: DocumentType[],
    module: string
}

export interface ISortConfig {
    field: string,
    dir: string
}

export interface User extends IEntity {
    fullName: string,    
    city: string,
    officeLocation: string,
    email: string,
    supervisor: string,
    deptId: number,
    employeeType: number,
    team: string
}

export interface UserExtended {
    firstName: string,
    lastName: string,    
    role: string,    
    department: string,
    status: string,
    lastLoginDate: Date,
    mobile: string,
    employeeType: string,
    company: string,
    jobTitle: string,    
    division: string
}

export interface UserDocumentAction {
    lastAction: string,
    notifyDate: Date,
    acknowledgedDate: Date,
    clarificationDate: Date,
    clarificationText: string,
    feedbackText: string,
    viewDate: Date
}

export interface IResultList {
    list: any[],
    totalResults: number
}

export interface UsersDocumentsResult extends IResultList {
    list: UserDocument<DocumentMetadataExtended>[]
}

export interface D2DocumentsResult extends IResultList {
    list: D2Document<DocumentMetadataExtended>[]
}

export interface LoginHistoryReportResult extends IResultList {
    list: LoginHistory[]
}

export interface UserDocument<DocumentMetadataType> extends IEntity {
    docuement?: Document<DocumentMetadataType>,
    user?: User,
    action?: UserDocumentAction
}

export interface D2Document<DocumentMetadataType> extends IEntity {
    docuement?: Document<DocumentMetadataType>
}

export interface DocumentsFilter {
    ids: number[],
    typeIds: number[],
    statuses: CleverString[],
    publishedDateFrom?: Date,
    publishedDateTo?: Date,
    publisherIds: number[]
}

export interface UsersFilter {
    ids: number[],
    supervisorIds: number[],
    statuses: string[],
    departments: string[],
    employeeTypes: string[],
    parentDepartments: string[]
}

export interface ExportColumn {
    title: string,
    name: string,   
    selected: boolean,
    formatter?: (value: any) => JSX.Element,
    defaultForComplianceReport?: number,
    infoDialog?: (item: UserDocument<DocumentMetadataExtended>) => void,
    unsortable: boolean
}

interface IPagedFilter {
    pageNumber: number,
    pageSize: number,
    sort: ISortConfig[]
}

export interface UsersDocumentsFilter extends IPagedFilter {
    users?: UsersFilter,
    documents?: DocumentsFilter,
    fileType: string,
    exportColumns: ExportColumn[]
}

export interface Location {
    id: number,
    name: string
}

export const getUsers = async () => {
    return await http<User[]>(getUsersPath(), "GET")
        .catch((e) => {
            console.log('error getting users')
            console.log(e);
            return null;
        });
};

export const getSupervisors = async () => {
    return await http<User[]>(getSupervisorsPath(), "GET")
        .catch((e) => {
            console.log('error getting supervisors')
            console.log(e);
            return null;
        });
};

export const getPublishers = async () => {
    return await http<User[]>(getPublishersPath(), "GET")
        .catch((e) => {
            console.log('error getting publishers')
            console.log(e);
            return null;
        });
};

export const getUsersSupervisedByCurrentUser = async () => {
    return await http<User[]>(getUsersSupervisedByCurrentUserPath(), "GET")
        .catch((e) => {
            console.log('error getting users in my team')
            console.log(e);
            return null;
        });
};

export const getUsersBySupervisor = async (supervisorId: number) => {
    return await http<User[]>(getUsersBySupervisorPath(supervisorId), "GET")
        .catch((e) => {
            console.log('error getting users for supervisor')
            console.log(e);
            return null;
        });
};

export const getDocumentTypes = async () => {
    return await http<DocumentType[]>(getDocumentTypesPath(), "GET")
        .catch((e) => {
            console.log('error getting document types')
            console.log(e);
            return null;
        });
};

export const getDepartments = async () => {
    return await http<string[]>(getDepartmentsPath(), "GET")
        .catch((e) => {
            console.log('error getting departments')
            console.log(e);
            return null;
        });
};

export const getParentDepartments = async () => {
    return await http<string[]>(getParentDepartmentsPath(), "GET")
        .catch((e) => {
            console.log('error getting parent departments')
            console.log(e);
            return null;
        });
};

export const getUserStatuses = async () => {
    return await http<string[]>(getUserStatusesPath(), "GET")
        .catch((e) => {
            console.log('error getting user statuses')
            console.log(e);
            return null;
        });
};

export const getDocumentStatuses = async () => {
    var result = await http<string[]>(getDocumentStatusesPath(), "GET")
        .catch((e) => {
            console.log('error getting document statuses')
            console.log(e);
            return null;
        });

    if (result) {
        return result.map(status => { return new CleverString(status); });
    }

    return null;
};

export const getUsersByDepartment = async (name: string) => {
    const result = await http<User[]>(getUsersByDepartmentPath(name), "GET")
        .catch((e) => {
            console.log('error getting users for depoartment')
            console.log(e);
            return null;
        });

    return result;
};

export const getUsersByStatus = async (name: string) => {
    return await http<User[]>(getUsersByStatusPath(name), "GET")
        .catch((e) => {
            console.log('error getting users for status')
            console.log(e);
            return null;
        });
};

export const getUsersByName = async (name: string) => {
    return await http<User[]>(getUsersByNamePath(name), "GET")
        .catch((e) => {
            console.log('error search users')
            console.log(e);
            return null;
        });
};

export const getSupervisorsByName = async (name: string) => {
    return await http<User[]>(getSupervisorsByNamePath(name), "GET")
        .catch((e) => {
            console.log('error search supervisors')
            console.log(e);
            return null;
        });
};

export const getUsersByPublisherName = async (name: string) => {
    return await http<User[]>(getUsersByPublisherNamePath(name), "GET")
        .catch((e) => {
            console.log('error search users')
            console.log(e);
            return null;
        });
};

export const getDocumentsByName = async (name: string) => {
    return await http<Document<DocumentMetadata>[]>(getDocumentsByNamePath(name), "GET")
        .catch((e) => {
            console.log('error searching documents')
            console.log(e);
            return null;
        });
};

export const getAllDocuments = async () => {
    return await http<Document<DocumentMetadata>[]>(getAllDocumentsPath(), "GET")
        .catch((e) => {
            console.log('error searching documents')
            console.log(e);
            return null;
        });
};

export const getAllD2Documents = async () => {
    return await http<Document<DocumentMetadata>[]>(getAllD2DocumentsPath(), "GET")
        .catch((e) => {
            console.log('error searching documents')
            console.log(e);
            return null;
        });
};

export const getUsersDocuments = async (filter: UsersDocumentsFilter) => {
    return await http<UsersDocumentsResult>(getUsersDocumentsPath(), 'POST', filter);
};

export const getD2Documents = async (filter: UsersDocumentsFilter) => {
    return await http<D2DocumentsResult>(getD2DocumentsPath(), 'POST', filter);
};

export const downloadResults = async (filter: UsersDocumentsFilter) => {
    const response = await httpDownloadAs(downloadResultsPath(), "POST", filter)
        .catch((e) => {
            console.log('error exporting users documents')
            console.log(e);
            return null;
        });

    return response;
};

export const downloadFatigueAssessmentResults = async (filter: FatigueAssessmentReportQuery) => {
    const response = await httpDownloadAs(downloadFatigueAssessmentResultsPath(), "POST", filter)
        .catch((e) => {
            console.log('error exporting fatigue assessments')
            console.log(e);
            return null;
        });

    return response;
};

export const getLoginHistoryResults = async (filter: LoginHistoryReportQuery) => {
    const response = await http<LoginHistoryReportResult>(getLoginHistoryResultsPath(), "POST", filter)
        .catch((e) => {
            console.log('error exporting login history')
            console.log(e);
            return null;
        });

    return response;
};

export const downloadLoginHistoryResults = async (filter: LoginHistoryReportQuery) => {
    const response = await httpDownloadAs(downloadLoginHistoryResultsPath(), "POST", filter)
        .catch((e) => {
            console.log('error exporting login history')
            console.log(e);
            return null;
        });

    return response;
};

export const downloadFullComplianceReport = async () => {
    const response = await httpDownloadAs(downloadFullComplianceReportPath(), "GET")
        .catch((e) => {
            console.log('error exporting login history')
            console.log(e);
            return null;
        });

    return response;
};

export async function getAllDocumentDepartments(): Promise<DocumentDepartment[]> {
    return await http<DocumentDepartment[]>(getParentDepartmentsPath.toString(), "GET").catch((e) => []);
}

export const downloadGNHExemptionResults = async (filter: GNHExemptionReportQuery) => {
    const response = await httpDownloadAs(downloadGNHExemptionResultsPath(), "POST", filter)
        .catch((e) => {
            console.log('error exporting GNH Exemptions')
            console.log(e);
            return null;
        });

    return response;
};

export const downloadTakeFiveResults = async (filter: TakeFiveReportQuery) => {
    const response = await httpDownloadAs(downloadTakeFiveResultsPath(), "POST", filter)
        .catch((e) => {
            console.log('error exporting Take 5s')
            console.log(e);
            return null;
        });

    return response;
};
export const downloadTrainEventReport = async (filter: TrainEventRecordReportQuery) => {
    const response = await httpDownloadAs(downloadTrainEventReportPath(), "POST", filter)
        .catch((e) => {
            console.log('error exporting GNH Exemptions')
            console.log(e);
            return null;
        });

    return response;
};
